import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Download } from "lucide-react";
import { CHARACTERS, getCharacter, COLORING_PAGES } from "@/data/characters";
import { Bubbles } from "@/components/site/Bubbles";

const Strip = ({ title, items, theme, id }) => (
  <section className="mt-14" data-testid={id}>
    <h2 className="inline-block rounded-full px-5 py-2 font-display text-lg font-semibold text-white" style={{ background: theme.deep }}>{title}</h2>
    <div className="no-scrollbar -mx-4 mt-6 flex gap-5 overflow-x-auto px-4 pb-4">
      {items.map((it, i) => (
        <motion.figure key={it.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
          whileHover={{ y: -8, rotate: i % 2 ? 2 : -2 }} className="w-40 shrink-0 rounded-3xl border-2 bg-white p-3 text-center sm:w-48" style={{ borderColor: theme.soft }}>
          <img src={it.src} alt={it.label} className="aspect-square w-full rounded-2xl object-contain" loading="lazy" />
          <figcaption className="mt-2 font-display text-sm font-semibold" style={{ color: theme.ink }}>{it.label}</figcaption>
        </motion.figure>
      ))}
    </div>
  </section>
);

const Fact = ({ label, value, theme }) => (
  <div className="rounded-2xl bg-white/80 p-4">
    <p className="text-xs font-bold uppercase tracking-widest" style={{ color: theme.main }}>{label}</p>
    <p className="mt-1 font-semibold" style={{ color: theme.ink }}>{value}</p>
  </div>
);

export default function CharacterPage() {
  const { slug } = useParams();
  const c = getCharacter(slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!c) return <Navigate to="/" replace />;
  const t = c.theme;
  const page = COLORING_PAGES.find((p) => p.slug === c.slug);
  const others = CHARACTERS.filter((o) => o.slug !== c.slug);

  return (
    <main data-testid={`character-page-${c.slug}`} style={{ background: `linear-gradient(180deg, ${t.soft} 0%, #ffffff 60%)` }}>
      <section className="relative overflow-hidden">
        <Bubbles count={8} />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 pb-10 pt-8 sm:px-8 lg:grid-cols-2 lg:pt-14">
          <div>
            <Link to="/#friends" data-testid="character-back-link" className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 font-display font-semibold shadow-sm transition-transform hover:-translate-x-1" style={{ color: t.deep }}>
              <ArrowLeft className="h-4 w-4" /> All friends
            </Link>
            <p className="mt-8 w-fit rounded-full px-4 py-1 font-display text-sm font-semibold text-white" style={{ background: t.main }}>{c.role}</p>
            <h1 data-testid="character-name" className="logo-text mt-4 text-6xl sm:text-7xl lg:text-8xl" style={{ "--logo-edge": t.deep }}>{c.name}</h1>
            <p className="mt-6 font-display text-2xl font-semibold" style={{ color: t.deep }}>{c.tagline}</p>
            <p data-testid="character-bio" className="mt-4 max-w-xl text-lg text-slate-600">{c.bio}</p>
            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              <Fact label="Type" value={c.type} theme={t} />
              <Fact label="Look" value={c.look} theme={t} />
              <Fact label="Personality" value={c.personality} theme={t} />
            </div>
          </div>
          <motion.img initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} src={c.portrait} alt={c.name}
            className="wobble w-full rounded-[2.5rem] border-[6px] border-white object-cover shadow-[0_30px_60px_rgba(15,23,42,0.18)]" />
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 pb-10 sm:px-8">
        <Strip title="Expressions" items={c.expressions} theme={t} id="character-expressions" />
        <Strip title="Action Poses" items={c.poses} theme={t} id="character-poses" />

        <section data-testid="character-signature" className="mt-16 grid items-center gap-8 overflow-hidden rounded-[2.5rem] p-8 text-white sm:p-12 md:grid-cols-[1fr_1.2fr]" style={{ background: t.deep }}>
          <img src={c.signature.src} alt={c.signature.title} className="mx-auto w-full max-w-sm rounded-3xl bg-white p-2" />
          <div>
            <p className="font-display text-lg font-semibold opacity-80">Signature moment · {c.signature.title}</p>
            <p className="mt-3 font-display text-4xl font-bold sm:text-5xl">"{c.signature.line}"</p>
            {page && (
              <a href={page.pdf} download data-testid="character-coloring-download" className="mt-8 inline-flex min-h-[56px] items-center gap-2 rounded-full bg-white px-6 font-display text-lg font-semibold transition-transform hover:scale-105" style={{ color: t.deep }}>
                <Download className="h-5 w-5" /> Color {c.name}!
              </a>
            )}
          </div>
        </section>

        <section className="mt-16">
          <h2 className="font-display text-2xl font-bold" style={{ color: t.deep }}>Official character sheet</h2>
          <img data-testid="character-sheet-image" src={c.sheet} alt={`${c.name} character reference sheet`} className="mt-6 w-full rounded-[2rem] border-4 border-white shadow-[0_20px_50px_rgba(15,23,42,0.12)]" loading="lazy" />
        </section>

        <section className="mt-16 flex flex-wrap items-center gap-4">
          <p className="font-display text-xl font-semibold text-slate-600">Meet more friends:</p>
          {others.map((o) => (
            <Link key={o.slug} to={`/characters/${o.slug}`} data-testid={`character-other-${o.slug}`} className="rounded-full px-6 py-3 font-display text-lg font-semibold text-white transition-transform hover:scale-105" style={{ background: o.theme.deep }}>
              {o.name}
            </Link>
          ))}
        </section>
      </div>
    </main>
  );
}
